/* eslint-disable max-len */
import { API_BASE_URL, API_TAG_TYPES, API_VERSION, JOB_MANAGER_API_HEADERS } from 'constants/common';
import { getUrlSearchParamsWithArrays } from 'utils/apiUtils';
import { baseApi } from 'services/api';
import {
  CancelSubscriptionResponse,
  CheckEstimateAttribute,
  CheckoutSessionDetails,
  CheckoutSessionResponse,
  CompleteSubscriptionResponse,
  CreateCheckoutSessionRequest,
  CreateEstimateNotePayload,
  CreateEstimateNoteResponse,
  CreateEstimatePayload,
  CreateEstimateV2Response,
  DeleteEstimateNotePayload,
  DeleteEstimateNoteResponse,
  EditEstimateNotePayload,
  EditEstimateNoteResponse,
  EstimateByIdResponse,
  EstimatePDFPreviewPayload,
  EstimateReferenceCheckResponse,
  GetEstimateAttachmentsResponse,
  GetEstimateSummaryResponse,
  GetEstimateTimelineResponse,
  GetEstimatesParams,
  GetEstimatesResponse,
  GetSubscriptionPlansResponse,
  GetTncSuggestionsResponse,
  InitiateSubscriptionPayload,
  InitiateSubscriptionResponse,
  ParseEstimatePayload,
  ParseEstimateResponse,
  RenewSubscriptionResponse,
  ShareEstimatePayload,
  TeamLevelEstimateTncSettings,
  UpdateEstimateDescriptionResponse,
  UpdateEstimateDetailRemindersPayload,
  UpdateEstimateDetailRemindersResponse,
  UpdateEstimateLeadSourcePayload,
  UpdateEstimateLeadSourceResponse,
  UpdateEstimateTncPayload,
  UpdateEstimateTncResponse,
  UpdateEstimateTncStatusPayload,
  UpdateEstimateV2Response
} from './estimates.model';
import { UploadImageResponse } from 'containers/jobs/jobs.model';
import {
  initializeEstimateForEdit,
  initializeEstimateFromAutofill,
  updateIsInitialisingEstimateFromAPI
} from './estimatesSlice';
import { downloadFile } from 'utils/commonUtils';
import { FileTypes } from 'types/baseTypes';
import {
  initialiseInvoiceSliceFromEstimate,
  updateIsInitialisingInvoiceSliceData
} from 'containers/invoices/InvoicesSlice';
import {
  initialiseJobDetailsFromEstimate,
  updateIsInitialisingJobFromAPI
} from 'containers/jobs/create-edit-job/createJobSlice';
import { UnlinkSourceType } from './constants';
import { FeatureToggleState } from 'containers/settings/constants';

const EstimatesBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    createEstimate: builder.mutation<CreateEstimateV2Response, { teamId: number; payload: CreateEstimatePayload }>({
      query: ({ teamId, payload }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V3}/teams/${teamId}/estimates`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to create estimate.',
        showToast: false
      }
    }),

    editEstimate: builder.mutation<
      UpdateEstimateV2Response,
      { teamId: number; estimateId: string; payload: CreateEstimatePayload }
    >({
      query: ({ teamId, estimateId, payload }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V3}/teams/${teamId}/estimates/${estimateId}`,
        method: 'PUT',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to update estimate.',
        showToast: false
      }
    }),

    uploadEstimateImageFile: builder.mutation<UploadImageResponse, { file: any; teamId: number }>({
      query: ({ teamId, file }) => {
        const formData = new FormData();
        formData.append('teamId', teamId.toString());
        formData.append('image', file);
        return {
          url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/attachment/upload`,
          method: 'POST',
          body: formData,
          headers: {
            // The browser will set the Content-Type header with the correct boundary
          }
        };
      }
    }),

    getEstimateById: builder.query<
      EstimateByIdResponse,
      { teamId: number; estimateId: string; isFromEdit?: boolean; isFromInvoice?: boolean; isFromJob?: boolean }
    >({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V2}/teams/${teamId}/estimates/${estimateId}`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      }),
      async onQueryStarted(
        { isFromEdit = false, isFromInvoice = false, isFromJob = false },
        { dispatch, queryFulfilled }
      ) {
        if (isFromEdit) {
          try {
            dispatch(updateIsInitialisingEstimateFromAPI(true));
            const { data } = await queryFulfilled;
            dispatch(initializeEstimateForEdit(data.data));
          } catch (error) {
            // console.error('Failed to initialize estimate data:', error);
          } finally {
            dispatch(updateIsInitialisingEstimateFromAPI(false));
          }
        }

        if (isFromInvoice) {
          try {
            dispatch(updateIsInitialisingInvoiceSliceData(true));
            const { data } = await queryFulfilled;
            dispatch(initialiseInvoiceSliceFromEstimate(data.data));
          } catch (error) {
            // console.error('Failed to initialize invoice data from estimate:', error);
          } finally {
            dispatch(updateIsInitialisingInvoiceSliceData(false));
          }
        }

        if (isFromJob) {
          try {
            dispatch(updateIsInitialisingJobFromAPI(true));
            const { data } = await queryFulfilled;
            dispatch(initialiseJobDetailsFromEstimate(data.data));
          } catch (error) {
            // console.error('Failed to initialize job data from estimate:', error);
          } finally {
            dispatch(updateIsInitialisingJobFromAPI(false));
          }
        }
      }
    }),

    getEstimates: builder.query<GetEstimatesResponse, GetEstimatesParams>({
      query: params => {
        const { teamId, ...queryParams } = params;
        const urlParams = getUrlSearchParamsWithArrays(queryParams);
        const url = `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates?${urlParams}`;
        return {
          url: url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to fetch estimates data',
        showToast: true
      }
    }),

    updateEstimateExpiryDate: builder.mutation<void, { teamId: number; estimateId: string; expiresAt: string }>({
      query: ({ teamId, estimateId, expiresAt }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/expires-at`,
        method: 'PATCH',
        body: { expiresAt }
      }),
      extraOptions: {
        success: 'Estimate expiry date updated successfully!',
        failure: 'Failed to update estimate expiry date',
        showToast: true
      }
    }),

    updateEstimateDescription: builder.mutation<
      UpdateEstimateDescriptionResponse,
      { teamId: number; estimateId: string; description: string }
    >({
      query: ({ teamId, estimateId, description }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/description`,
        method: 'PATCH',
        body: { description }
      }),
      extraOptions: {
        success: 'Estimate description updated successfully!',
        failure: 'Failed to update estimate description',
        showToast: true
      }
    }),

    updateEstimateTnc: builder.mutation<{ success: boolean }, { teamId: number; estimateId: string; tnc: string }>({
      query: ({ teamId, estimateId, tnc }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/tnc`,
        method: 'PATCH',
        body: { tnc }
      }),
      extraOptions: {
        success: 'Terms & conditions updated successfully!',
        failure: 'Failed to update terms & conditions',
        showToast: true
      }
    }),
    updateEstimateNote: builder.mutation<{ success: boolean }, { teamId: number; estimateId: string; comment: string }>(
      {
        query: ({ teamId, estimateId, comment }) => ({
          url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/note`,
          method: 'PATCH',
          body: { comment }
        }),
        extraOptions: {
          success: 'Note updated successfully!',
          failure: 'Failed to update note',
          showToast: true
        }
      }
    ),

    updateEstimateAttachments: builder.mutation<
      { success: boolean },
      { teamId: number; estimateId: string; attachments: [] }
    >({
      query: ({ teamId, estimateId, attachments }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/attachments`,
        method: 'PATCH',
        body: { attachments }
      }),
      extraOptions: {
        success: 'Attachments updated successfully!',
        failure: 'Failed to update attachments',
        showToast: true
      }
    }),

    shareEstimate: builder.mutation<void, ShareEstimatePayload>({
      query: ({ teamId, estimateId, phoneNumber, email, type, updateCustomer }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/share?type=${type}`,
        method: 'POST',
        body: { email, phoneNumber, updateCustomer, teamId }
      }),
      extraOptions: {
        failure: 'Failed to share estimate',
        showToast: false
      }
    }),

    downloadEstimateAsPDF: builder.query<Blob, { teamId: number; estimateId: string }>({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/download`,
        method: 'GET',
        responseHandler: async response => {
          await downloadFile({ response, fileType: FileTypes.PDF, fileName: 'estimate' });
        }
      }),
      extraOptions: {
        failure: 'Failed to download estimate',
        success: 'Estimate downloaded successfully!',
        showToast: true
      }
    }),

    getEstimateTimeline: builder.query<
      GetEstimateTimelineResponse,
      { teamId: number; estimateId: string; timezone: string }
    >({
      query: ({ teamId, estimateId, timezone }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/timeline?timezone=${timezone}`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch estimate timeline',
        showToast: true
      }
    }),

    createEstimateNote: builder.mutation<CreateEstimateNoteResponse, CreateEstimateNotePayload>({
      query: ({ teamId, estimateId, attachments, comment }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/notes`,
        method: 'POST',
        body: { comment, attachments }
      }),
      extraOptions: {
        failure: 'Failed to add note',
        showToast: true
      }
    }),

    editEstimateNote: builder.mutation<EditEstimateNoteResponse, EditEstimateNotePayload>({
      query: ({ teamId, estimateId, noteId, comment, attachments }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/notes/${noteId}`,
        method: 'PUT',
        body: { comment, attachments }
      }),
      extraOptions: {
        success: 'Note updated successfully!',
        failure: 'Failed to update note',
        showToast: true
      }
    }),

    deleteEstimateNote: builder.mutation<DeleteEstimateNoteResponse, DeleteEstimateNotePayload>({
      query: ({ estimateId, teamId, noteId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/notes/${noteId}`,
        method: 'DELETE'
      }),
      extraOptions: {
        failure: 'Failed to delete note',
        showToast: true
      }
    }),

    deleteEstimate: builder.mutation<{ success: boolean; message: string }, { teamId: number; estimateId: string }>({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}`,
        method: 'DELETE'
      }),
      extraOptions: {
        failure: 'Failed to delete estimate',
        showToast: false
      }
    }),

    markEstimateAsExpired: builder.mutation<void, { teamId: number; estimateId: string }>({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/expire`,
        method: 'PATCH'
      }),
      extraOptions: {
        success: 'Estimate marked as expired',
        failure: 'Failed to mark estimate as expired',
        showToast: true
      }
    }),

    archiveEstimate: builder.mutation<void, { teamId: number; estimateId: string }>({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/archive`,
        method: 'PATCH'
      }),
      extraOptions: {
        success: 'Estimate archived successfully',
        failure: 'Failed to archive estimate',
        showToast: true
      }
    }),

    updateEstimateStatus: builder.mutation<
      { success: boolean; message: string },
      { teamId: number; estimateId: string; status: string; comment?: string }
    >({
      query: ({ teamId, estimateId, status, comment = '' }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/status`,
        method: 'PATCH',
        body: { status, comment }
      }),
      extraOptions: {
        success: 'Estimate status updated successfully',
        failure: 'Failed to update estimate status',
        showToast: true
      }
    }),

    downloadEstimate: builder.query<Blob, { teamId: number; estimateId: string }>({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/download`,
        method: 'GET',
        responseHandler: async response => {
          await downloadFile({
            response,
            fileName: 'estimate',
            fileType: FileTypes.PDF,
            isFileNameWithExtension: true
          });
        },
        // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
        cache: 'no-cache'
      }),
      extraOptions: {
        failure: 'Failed to download estimate',
        success: 'Download completed successfully',
        showToast: true
      }
    }),

    // New endpoint for exporting estimates
    exportEstimates: builder.query<Blob, GetEstimatesParams>({
      query: params => {
        const { teamId, ...queryParams } = params;
        const urlParams = getUrlSearchParamsWithArrays(queryParams);
        const url = `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/export?${urlParams}`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({
              response,
              fileType: FileTypes.CSV,
              fileName: 'estimates'
            });
          }
        };
      },
      extraOptions: {
        failure: 'Failed to export estimates',
        success: 'Export completed successfully',
        showToast: true
      }
    }),

    // New endpoint for updating estimate assignees
    updateEstimateAssignees: builder.mutation<
      { success: boolean; message: string },
      { teamId: number; estimateId: string; assignees: string[] }
    >({
      query: ({ teamId, estimateId, assignees }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/assignees`,
        method: 'PATCH',
        body: { assignees }
      }),
      extraOptions: {
        success: 'Estimate assignees updated successfully!',
        failure: 'Failed to update estimate assignees',
        showToast: true
      }
    }),

    // New endpoint for disconnecting an estimate from a job/invoice
    disconnectJobOrInvoiceFromEstimate: builder.mutation<
      { success: boolean; message: string },
      {
        teamId: number;
        estimateId: string;
        entityType: 'JOB' | 'INVOICE';
        entityID: string;
        sourceType: UnlinkSourceType;
      }
    >({
      query: ({ teamId, estimateId, entityType, entityID, sourceType }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/disconnect?source=${sourceType}`,
        method: 'PATCH',
        body: { entityType, entityID }
      }),
      invalidatesTags: [API_TAG_TYPES.INVOICE_BY_ID],
      extraOptions: {
        failure: 'Failed to disconnect estimate',
        showToast: false
      }
    }),

    checkEstimateReferenceNumber: builder.mutation<EstimateReferenceCheckResponse, CheckEstimateAttribute>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/check-attribute`,
        method: 'POST',
        body
      }),
      extraOptions: {
        failure: 'Failed to check estimate reference number',
        showToast: false
      }
    }),

    updateEstimateLeadSource: builder.mutation<UpdateEstimateLeadSourceResponse, UpdateEstimateLeadSourcePayload>({
      query: ({ leadSourceType, leadSource, teamId, estimateId }) => {
        const url = `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/lead-source`;
        return {
          url,
          method: 'PATCH',
          body: {
            leadSourceType,
            leadSource
          }
        };
      },
      extraOptions: {
        failure: 'Failed to update estimate lead source',
        showToast: true
      }
    }),

    getEstimatePDFPreview: builder.mutation<string, EstimatePDFPreviewPayload>({
      query: ({ teamId, payload }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/preview`,
        method: 'POST',
        body: payload,
        responseHandler: async response => {
          const blob = await response.blob();
          const url = blob ? URL.createObjectURL(blob) : '';
          return url;
        },
        // Do not cache responses for pdf's as it may lead to crashing the browser
        cache: 'no-cache'
      }),
      extraOptions: {
        failure: 'Failed to generate estimate preview',
        showToast: true
      }
    }),

    getEstimateTncSuggestions: builder.query<GetTncSuggestionsResponse, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/tnc/suggestions`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch terms and conditions suggestions',
        showToast: true
      }
    }),

    getEstimateSummary: builder.query<GetEstimateSummaryResponse, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/summary`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch estimate summary',
        showToast: true
      }
    }),

    updateTeamLevelEstimateTnc: builder.mutation<TeamLevelEstimateTncSettings, UpdateEstimateTncPayload>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/tnc`,
        method: 'POST',
        body
      }),
      extraOptions: {
        success: 'Terms & conditions updated successfully!',
        failure: 'Failed to update terms & conditions',
        showToast: true
      }
    }),

    updateTeamLevelEstimateTncStatus: builder.mutation<UpdateEstimateTncResponse, UpdateEstimateTncStatusPayload>({
      query: ({ teamId, enabled }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/tnc/status`,
        method: 'PATCH',
        body: { enabled }
      }),
      extraOptions: {
        failure: 'Failed to update terms & conditions status',
        showToast: true
      }
    }),

    parseEstimate: builder.mutation<ParseEstimateResponse, { teamId: string; body: ParseEstimatePayload }>({
      query: ({ teamId, body }) => {
        return {
          url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/parse`,
          method: 'POST',
          body
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as any;
          const merchantCustomizations = state.rootReducer.app.merchantCustomizations;

          // Prepare data with signature enabled state
          const modifiedData = {
            ...data,
            data: {
              ...data.data,
              isSignatureEnabled:
                merchantCustomizations?.customerSignatureConfig === FeatureToggleState.ALWAYS_ON ||
                merchantCustomizations?.customerSignatureConfig === FeatureToggleState.ON_BY_DEFAULT
            }
          };

          // Dispatch action to populate estimate state
          dispatch(initializeEstimateFromAutofill(modifiedData));
        } catch (error) {
          // Error handling is already covered by extraOptions
        }
      },
      extraOptions: {
        failure: 'Failed to auto-fill estimate details',
        showToast: false
      }
    }),

    createSubscriptionCheckoutSession: builder.mutation<
      CheckoutSessionResponse,
      { teamId: number; payload: CreateCheckoutSessionRequest }
    >({
      query: ({ teamId, payload }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/teams/${teamId}/checkout`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to create checkout session',
        showToast: false
      }
    }),

    getCheckoutSession: builder.query<CheckoutSessionDetails, { teamId: number; sessionId: string }>({
      query: ({ teamId, sessionId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/teams/${teamId}/checkout/session/${sessionId}`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to retrieve checkout session',
        showToast: false
      }
    }),

    initiateSubscription: builder.mutation<
      InitiateSubscriptionResponse,
      { teamId: number; payload: InitiateSubscriptionPayload }
    >({
      query: ({ teamId, payload }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/teams/${teamId}/initiate`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to initiate subscription',
        showToast: false
      }
    }),

    completeSubscription: builder.mutation<
      CompleteSubscriptionResponse,
      { teamId: number; payload: InitiateSubscriptionPayload }
    >({
      query: ({ teamId, payload }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/teams/${teamId}/complete`,
        method: 'POST',
        body: payload
      }),
      extraOptions: {
        failure: 'Failed to complete subscription',
        showToast: false
      }
    }),

    getSubscriptionPlans: builder.query<GetSubscriptionPlansResponse, void>({
      query: () => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/plans?version=web`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch subscription plans',
        showToast: false
      }
    }),

    cancelSubscription: builder.mutation<CancelSubscriptionResponse, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/teams/${teamId}/cancel`,
        method: 'POST'
      }),
      extraOptions: {
        failure: 'Failed to cancel subscription',
        showToast: true
      }
    }),

    renewSubscription: builder.mutation<RenewSubscriptionResponse, { teamId: number }>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/subscription/teams/${teamId}/renew`,
        method: 'POST'
      }),
      extraOptions: {
        failure: 'Failed to renew subscription',
        showToast: true
      }
    }),

    getEstimateAttachments: builder.query<GetEstimateAttachmentsResponse, { teamId: number; estimateId: string }>({
      query: ({ teamId, estimateId }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/attachments`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch estimate attachments',
        showToast: false
      }
    }),

    updateEstimateReminders: builder.mutation<
      UpdateEstimateDetailRemindersResponse,
      UpdateEstimateDetailRemindersPayload
    >({
      query: ({ teamId, estimateId, reminders }) => ({
        url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/${estimateId}/reminders`,
        method: 'PATCH',
        body: { reminders }
      }),
      extraOptions: {
        success: 'Reminders updated successfully!',
        failure: 'Failed to update reminders',
        showToast: true
      }
    })
  })
});

export const {
  useCreateEstimateMutation,
  useEditEstimateMutation,
  useUploadEstimateImageFileMutation,
  useGetEstimateByIdQuery,
  useLazyGetEstimateByIdQuery,
  useGetEstimatesQuery,
  useLazyGetEstimatesQuery,
  useUpdateEstimateExpiryDateMutation,
  useUpdateEstimateDescriptionMutation,
  useShareEstimateMutation,
  useLazyDownloadEstimateAsPDFQuery,
  useCreateEstimateNoteMutation,
  useEditEstimateNoteMutation,
  useDeleteEstimateNoteMutation,
  useDeleteEstimateMutation,
  useMarkEstimateAsExpiredMutation,
  useArchiveEstimateMutation,
  useUpdateEstimateStatusMutation,
  useLazyDownloadEstimateQuery,
  useLazyExportEstimatesQuery,
  useUpdateEstimateAssigneesMutation,
  useLazyGetEstimateTimelineQuery,
  useDisconnectJobOrInvoiceFromEstimateMutation,
  useCheckEstimateReferenceNumberMutation,
  useUpdateEstimateLeadSourceMutation,
  useUpdateEstimateTncMutation,
  useUpdateEstimateNoteMutation,
  useUpdateEstimateAttachmentsMutation,
  useGetEstimatePDFPreviewMutation,
  useLazyGetEstimateTncSuggestionsQuery,
  useLazyGetEstimateSummaryQuery,
  useUpdateTeamLevelEstimateTncMutation,
  useUpdateTeamLevelEstimateTncStatusMutation,
  useCreateSubscriptionCheckoutSessionMutation,
  useGetCheckoutSessionQuery,
  useLazyGetCheckoutSessionQuery,
  useInitiateSubscriptionMutation,
  useCompleteSubscriptionMutation,
  useGetSubscriptionPlansQuery,
  useLazyGetSubscriptionPlansQuery,
  useCancelSubscriptionMutation,
  useRenewSubscriptionMutation,
  useGetEstimateAttachmentsQuery,
  useLazyGetEstimateAttachmentsQuery,
  useUpdateEstimateRemindersMutation,
  useParseEstimateMutation
} = EstimatesBaseApi;

export const estimatesApi = EstimatesBaseApi;
